import {
  type Tag,
  type Task,
  formatDayName,
  formatDayNumber,
  formatMonth,
} from "@taskfortress/core"
import React, { useCallback, useMemo } from "react"
import { useData } from "../../providers/DataProvider"
import { cn } from "../../styles/utils"
import { AddTaskButton } from "../AddTaskButton"
import { Box } from "../Box"
import { Text } from "../Text"
import { TextLink } from "../TextLink"
import { Droppable } from "../dragDrop"
import { DraggableTaskItem } from "./DraggableTaskItem"
import { TaskItem } from "./TaskItem"
import {
  TASK_ITEM_HEIGHT,
  TASK_ITEM_HEIGHT_CLASSNAME,
  TASK_ITEM_MAX_HEIGHT_CLASSNAME,
} from "./constants"
import { groupTasksByStatus } from "./taskGrouping"

interface DroppableDayColumnProps {
  date: string
  tasks: Task[]
  allTasks: Task[]
  dragState: {
    draggedTaskId: string | null
    draggedTask: Task | null
    sourceDate: string | null
    originalIndex: number | null
    hoverDate: string | null
    hoverIndex: number | null
    wasSuccessfulDrop: boolean
  }
  selectedTag?: Tag | null
  className?: string
  width: number
}

interface TaskDropZoneProps {
  id: string
  position: number
  date: string
  dragState: DroppableDayColumnProps["dragState"]
  className?: string
  activeClassName?: string
}

const TaskDropZone: React.FC<TaskDropZoneProps> = ({
  id,
  position,
  date,
  dragState,
  className,
  activeClassName,
}) => {
  const isActive = useMemo(
    () =>
      dragState.draggedTaskId &&
      dragState.hoverDate === date &&
      dragState.hoverIndex === position,
    [
      dragState.draggedTaskId,
      dragState.hoverDate,
      dragState.hoverIndex,
      date,
      position,
    ],
  )

  return (
    <Droppable
      id={id}
      data={{ type: "todo-insert", position, date }}
      className={cn(
        "border-dashed border-2 border-blue-500 rounded-lg transition-all duration-200 ease-out",
        TASK_ITEM_HEIGHT_CLASSNAME,
        className,
        isActive ? "opacity-100" : "opacity-0 pointer-events-none",
        isActive && activeClassName,
      )}
    />
  )
}

export const DroppableDayColumn: React.FC<DroppableDayColumnProps> = ({
  date,
  tasks,
  allTasks,
  dragState,
  selectedTag,
  className,
  width,
}) => {
  const { setSelectedTasksTag } = useData()

  // Group tasks by status for rendering
  const tasksByStatus = groupTasksByStatus(tasks)

  // Calculate hidden tasks count
  const hiddenTasksCount = useMemo(() => {
    return allTasks.length - tasks.length
  }, [allTasks.length, tasks.length])

  // Handle clearing tag filter
  const handleClearTagFilter = useCallback(() => {
    setSelectedTasksTag(null)
  }, [setSelectedTasksTag])

  // Calculate which tasks should be displaced and how
  const getTaskDisplacement = useCallback(
    (taskIndex: number): number => {
      const {
        draggedTaskId,
        sourceDate,
        originalIndex,
        hoverDate,
        hoverIndex,
      } = dragState

      if (!draggedTaskId || hoverDate !== date || hoverIndex === null) {
        return 0 // No displacement if not hovering over this date
      }

      // If this is the dragged task and it's from the same date, don't displace it
      const task = tasksByStatus.todo[taskIndex]
      if (task?.id === draggedTaskId && sourceDate === date) {
        return 0
      }

      // Calculate displacement for cross-day and intra-day drops
      let displacement = 0

      if (sourceDate === date && originalIndex !== null) {
        // Intra-day reordering logic
        if (hoverIndex < originalIndex) {
          // Dragging backward: items between hoverIndex and originalIndex-1 shift forward
          if (taskIndex >= hoverIndex && taskIndex < originalIndex) {
            displacement = 1 // Move down one position
          }
        } else if (hoverIndex > originalIndex) {
          // Dragging forward: items between originalIndex+1 and hoverIndex shift backward
          if (taskIndex > originalIndex && taskIndex <= hoverIndex) {
            displacement = -1 // Move up one position
          }
        }
      } else if (sourceDate !== date) {
        // Cross-day drop: items at and after hoverIndex shift down
        if (taskIndex >= hoverIndex) {
          displacement = 1 // Move down one position
        }
      }

      return displacement
    },
    [dragState, date, tasksByStatus.todo],
  )

  return (
    <Box className={className} style={{ width }}>
      <Box className="h-full rounded-lg border border-outline-400 shadow-sm bg-background-0 overflow-hidden mx-3">
        <Box className="flex flex-row gap-2 justify-center items-center p-2 bg-primary-50 relative">
          <Text className="text-sm text-typography-900">
            {formatDayName(date)}
          </Text>
          <Text className="text-xl font-bold text-typography-900">
            {formatDayNumber(date)}
          </Text>
          <Text className="text-sm text-typography-600">
            {formatMonth(date)}
          </Text>
          <Box className="absolute bottom-2 right-2">
            <AddTaskButton date={date} selectedTag={selectedTag} small />
          </Box>
        </Box>

        <Box className="bg-tertiary-50 block h-full overflow-y-auto overflow-x-hidden p-2">
          {hiddenTasksCount > 0 && (
            <TextLink
              onPress={handleClearTagFilter}
              size="xs"
              textClassName="text-center"
              className="mb-2"
            >
              {hiddenTasksCount} task{hiddenTasksCount === 1 ? "" : "s"} hidden
            </TextLink>
          )}
          {tasks.length === 0 ? (
            <Box className="items-center justify-center py-10 relative">
              {dragState.draggedTaskId ? null : (
                <Text className="text-center text-typography-500 text-sm italic">
                  No active tasks
                  {hiddenTasksCount > 0 && ` (${hiddenTasksCount} hidden)`}
                </Text>
              )}

              {/* Add default drop zone for empty days */}
              {dragState.draggedTaskId && (
                <TaskDropZone
                  className={cn("absolute inset-0", TASK_ITEM_HEIGHT_CLASSNAME)}
                  id={`${date}-todo-0`}
                  position={0}
                  date={date}
                  dragState={dragState}
                />
              )}
            </Box>
          ) : (
            <>
              {/* TODO tasks with drop zones and displacement */}
              {tasksByStatus.todo.map((task, index) => {
                const displacement = getTaskDisplacement(index)
                const isDragged = task.id === dragState.draggedTaskId

                return (
                  <Box
                    key={task.id}
                    className={cn("mb-2 relative", TASK_ITEM_HEIGHT_CLASSNAME)}
                  >
                    <Box
                      className={cn(
                        "transition-transform duration-200 ease-out",
                        isDragged ? "opacity-0" : "opacity-100",
                      )}
                      style={{
                        transform: `translateY(${displacement * (TASK_ITEM_HEIGHT + 8) /* margin */}px)`,
                      }}
                    >
                      <DraggableTaskItem
                        task={task}
                        className={cn(
                          TASK_ITEM_HEIGHT_CLASSNAME,
                          TASK_ITEM_MAX_HEIGHT_CLASSNAME,
                        )}
                      />
                    </Box>

                    <TaskDropZone
                      className="absolute inset-0"
                      id={`${date}-todo-${index}`}
                      position={index}
                      date={date}
                      dragState={dragState}
                    />
                  </Box>
                )
              })}

              {/* Add final drop zone for dropping at the end of the todo list */}
              {dragState.draggedTaskId && dragState.sourceDate !== date && (
                <Box
                  className={cn("relative", TASK_ITEM_HEIGHT_CLASSNAME)}
                  style={{ minHeight: TASK_ITEM_HEIGHT }}
                >
                  <TaskDropZone
                    className="absolute inset-0"
                    id={`${date}-todo-${tasksByStatus.todo.length}`}
                    position={tasksByStatus.todo.length}
                    date={date}
                    dragState={dragState}
                  />
                </Box>
              )}

              {/* Display done tasks (not draggable) */}
              {tasksByStatus.done.length > 0 && (
                <>
                  <Box className="w-8/12 h-1 border-t mx-auto border-tertiary-700 my-4" />
                  {tasksByStatus.done.map((task) => (
                    <Box key={task.id} className="mb-1">
                      <TaskItem task={task} />
                    </Box>
                  ))}
                </>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}
